<template>
    <label class="f fv ath-form-number" :for="uid">
        <div class="f fh fa-c dw-10 sm-b-xs" v-if="lbl">
            <slot name="labelLeft"></slot>
            <span class="fg ath-form-number-label">{{ uiTitle }}<slot name="labelAfter"></slot></span>
            <slot name="labelRight"></slot>
        </div>
        <!-- Input -->
        <input ref="input"
            novalidate
            class="dw-10"
            :class="[uiReadonly ? 'c-na' : '']"
            @input="update"
            :value="value"
            type="number"
            :name="uid"
            :min="min"
            :max="max"
            :step="step"
            :id="uid"
            :placeholder="$options.filters.athT(placeholder)"
            :disabled="disabled" />
    </label>
</template>

<script>
import { guuid, isNumber, join } from '@aretheyhappy/utils';

export default {
    name: 'mead-form-number',
    props: {
        /**
         * Label i18n key rendered above the input. Only shown when truthy.
         */
        lbl         : {type: [String, Boolean], default: () => false},
        /**
         * Entity name interpolated into the label when translating it.
         */
        lblEntity   : {type: [Boolean, String], default: () => false},
        /**
         * Whether the interpolated entity name should be lowercased in the translated label.
         */
        lblEntityLc : {type: Boolean, default: () => true},
        /**
         * Placeholder text (i18n key) shown in the input when empty.
         */
        placeholder : {type: [String, Boolean], default: () => false},
        /**
         * Current numeric value (v-model).
         */
        value       : {type: Number, default: () => 0},
        /**
         * Disables the input.
         */
        disabled    : {type: Boolean, default: () => false},
        /**
         * Minimum allowed value; entered values are clamped up to this.
         */
        min         : {type: [Number, Boolean], default: () => 0},
        /**
         * Maximum allowed value; entered values are clamped down to this.
         */
        max         : {type: [Number, Boolean], default: () => 10},
        /**
         * Step increment/decrement applied by the native number input's spinners.
         */
        step        : {type: Number, default: () => 1},
        /**
         * Milliseconds to debounce the `input` emission by; `false` emits immediately.
         */
        debounce    : {type: [Number, Boolean], default: () => false},
        /**
         * Function invoked when the input receives focus, attached as a native `focus` listener.
         */
        uiOnFocus   : {type: [Boolean, Function], default: () => false},
        /**
         * Marks the input as readonly.
         */
        uiReadonly  : {type: Boolean, default: () => false},
        /**
         * Automatically focuses the input once mounted.
         */
        uiAutoFocus : {type: Boolean, default: () => false},
        /**
         * Appends an "(optional)" suffix to the label.
         */
        uiOptional  : {type: Boolean, default: () => false},
    },
    data () {
        return {
            uid                 : guuid(),
            ui_debounce         : isNumber(this.debounce) ? parseInt(this.debounce) : false,
            ui_debounce_timer   : false,
        };
    },
    methods: {
        update () {
            let val = parseInt(this.$refs.input.value);
            if (!isNumber(val)) val = this.min;

            //  If min is set and val is smaller than min, set val to min
            if (isNumber(this.min) && val < this.min) val = this.min;

            //  If max is set and val is smaller than max, set val to max
            if (isNumber(this.max) && val > this.max) val = this.max;

            this.$refs.input.value = val;

            //  Debounce
            if (this.ui_debounce === false) {
                this.$emit('input', val);
            } else {
                if (this.ui_debounce_timer) clearTimeout(this.ui_debounce_timer);
                this.ui_debounce_timer = setTimeout(
                    () => this.$emit('input', val),
                    this.ui_debounce
                );
            }
        },
    },
    computed: {
        uiTitle: function () {
            return join([
                this.lblEntity
                    ? MeadCommon.I18N[this.lblEntityLc ? 'actionLc' : 'action'](this.lbl, this.lblEntity)
                    : MeadCommon.I18N.go(this.lbl),
                this.uiOptional
                    ? `(${MeadCommon.I18N.go('mead.labels.optional')})`
                    : false,
            ], {delim: ' '});
        },
    },
    watch: {
        max: function () {
            this.update();
        },
        min: function () {
            this.update();
        },
    },
    async mounted () {
        //  Set readonly if uiReadonly is passed
        if (this.$refs.input && this.uiReadonly) {
            this.$refs.input.setAttribute('readonly', 'readonly');
        }

        //  UIAutoFocus
        if (this.$refs.input && this.uiAutoFocus) {
            await this.$nextTick();
            this.$refs.input.focus();
        }

        //  Add uiOnFocus
        if (this.$refs.input && this.uiOnFocus) {
            this.$refs.input.addEventListener('focus', this.uiOnFocus);
        }
    },
};
</script>

<style lang="scss" scoped>
@import '../../../../sass/_variables.scss';

input {
    font-family: $ff-b;
    font-weight: 400;
    font-size: $fs-s;
    height: 3rem;
    padding: 0 $s-s;
    border: none;
    border-radius: $br-m;
    line-height: initial;
    box-shadow: inset 0 0 4rem 4rem var(--c-brand-light-bg);
    background-color: var(--c-brand-light-bg);
    color: var(--c-brand-light-fg);

    &[disabled="disabled"] {
        cursor: not-allowed;
        opacity: .65;
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-internal-autofill-selected {
        color: var(--c-brand-light-fg) !important;
        -webkit-text-fill-color: var(--c-brand-light-fg) !important;
    }

    &:active,
    &:focus {
        border-color: transparent;
    }

    &.el--withcounter {
        padding-right: $s-l;
    }
}

.ath-form-number-label {
    font-family: $ff-b;
    font-weight: 400;
    line-height: $lh-xs;
    font-size: $fs-s;
    color: var(--c-txt);
}

.ath-form-number {
    &.el--invalid input {
        outline: 1px solid var(--c-brand-danger-bg);
    }
}

//  SIZES

@mixin inputSize ($name, $height) {
    .ath-form-number.el--s-#{$name} input {
        height: $height;
    }
}

@include inputSize('s', 3rem);
@include inputSize('m', 4rem);
@include inputSize('l', 5rem);
</style>
